"use client";

import { useRef } from "react";
import { cn } from "../../lib";
import type { MediaPlayerTheme } from "./media-player-theme";
import { MediaPlayerTrackGlow } from "./media-player-track-glow";
import {
	MEDIA_PLAYER_SEGMENT_DIVIDER_CLASSNAME,
	resolveMediaPlayerScrubHitWidthPercent,
	useMediaPlayerTrackSegmentTooltip,
} from "./media-player-track-segment-tooltip";
import { MediaPlayerTrackSegmentTooltipLabel } from "./media-player-track-segment-tooltip-label";
import {
	buildMediaPlayerWaveformHeights,
	resolveWaveformPlayedBarCount,
} from "./media-player-waveform-bars";
import { useMediaPlayerScrub } from "./use-media-player-scrub";

export interface MediaPlayerSeekSegment {
	percent: number;
	label: string;
	cardIndex?: number;
}

export interface MediaPlayerSeekBarProps {
	theme: MediaPlayerTheme;
	value: number;
	maxSeekPercent: number;
	furthestConsumedPercent?: number;
	segments?: MediaPlayerSeekSegment[];
	interactive: boolean;
	waveformSeed?: string;
	onPause: () => void;
	onSeekCommit: (percent: number) => void;
	onSeekRejected?: () => void;
	seekLockedLabel?: string;
	trackGlow?: boolean;
	className?: string;
}

const WAVE_HEIGHT_PX = 16;

export function MediaPlayerSeekBar({
	theme,
	value,
	maxSeekPercent,
	furthestConsumedPercent = maxSeekPercent,
	segments = [],
	interactive,
	waveformSeed = "topic-audio",
	onPause,
	onSeekCommit,
	onSeekRejected,
	seekLockedLabel = "Seek locked",
	trackGlow = true,
	className,
}: MediaPlayerSeekBarProps) {
	const trackRef = useRef<HTMLDivElement>(null);
	const scrubHitWidthPercent =
		resolveMediaPlayerScrubHitWidthPercent(maxSeekPercent);
	const barHeights = buildMediaPlayerWaveformHeights(waveformSeed);
	const {
		playheadPercent,
		onPointerDown,
		onPointerMove,
		onPointerUp,
		onPointerLeave,
		onPointerCancel,
	} = useMediaPlayerScrub({
		progressPercent: value,
		maxSeekPercent,
		interactive,
		trackBoundsRef: trackRef,
		onPause,
		onSeekCommit,
		onSeekRejected,
	});
	const {
		trackTooltipLabel,
		trackTooltipAnchorCenterPercent,
		mergePointerHandlers,
	} = useMediaPlayerTrackSegmentTooltip(segments, trackRef);
	const trackPointerHandlers = mergePointerHandlers({
		onPointerDown,
		onPointerMove,
		onPointerUp,
		onPointerLeave,
		onPointerCancel,
	});
	const playedCount = resolveWaveformPlayedBarCount(
		playheadPercent,
		barHeights.length,
	);
	const lockedStartPercent = Math.max(
		furthestConsumedPercent,
		maxSeekPercent,
	);
	const scrubHitActive = interactive;

	return (
		<div
			ref={trackRef}
			className={cn(
				"relative z-[1] w-full touch-none select-none",
				trackGlow
					? "flex min-h-11 items-center overflow-visible py-2"
					: "h-4",
				className,
			)}
			role="slider"
			aria-valuemin={0}
			aria-valuemax={100}
			aria-valuenow={Math.round(playheadPercent)}
			aria-disabled={!interactive}
		>
			<div
				className={cn(
					"relative z-[1] w-full",
					trackGlow ? "h-4 shrink-0 overflow-visible" : "h-full",
				)}
			>
				{trackGlow ? (
					<MediaPlayerTrackGlow
						playheadPercent={playheadPercent}
						className="-top-4 -bottom-4"
						color1={theme.trackGlowColor1}
						color2={theme.trackGlowColor2}
					/>
				) : null}
				{lockedStartPercent < 100 ? (
					<div
						className={cn(
							"pointer-events-none absolute inset-y-0 right-0 rounded-full",
							theme.seekBarLockedZoneClassName,
						)}
						style={{
							left: `${lockedStartPercent}%`,
						}}
						aria-hidden
					/>
				) : null}
				<svg
					className="pointer-events-none h-full w-full"
					viewBox={`0 0 ${barHeights.length * 10} ${WAVE_HEIGHT_PX}`}
					preserveAspectRatio="none"
					aria-hidden="true"
				>
					{barHeights.map((height, index) => {
						const barHeight = height * (WAVE_HEIGHT_PX - 4);
						const y = WAVE_HEIGHT_PX - barHeight;
						const isPlayed = index < playedCount;
						return (
							<rect
								key={`bar-${index}`}
								x={index * 10 + 2}
								y={y}
								width={6}
								height={barHeight}
								rx={2}
								className={cn(
									isPlayed
										? cn(
												theme.seekBarWaveformActiveClassName,
												theme.seekBarWaveformGlowClassName,
											)
										: "fill-white/25 dark:fill-white/15",
								)}
							/>
						);
					})}
				</svg>
				{segments.map((segment, index) => {
					const left = Math.max(0, Math.min(100, segment.percent));
					return (
						<div
							key={`segment-${segment.cardIndex ?? index}-${segment.label}`}
							className={cn(
								"pointer-events-none absolute top-0 bottom-0 bg-white/40",
								MEDIA_PLAYER_SEGMENT_DIVIDER_CLASSNAME,
							)}
							style={{
								left: `calc(${left}% - 1.5px)`,
							}}
							aria-hidden
						/>
					);
				})}
				{scrubHitActive ? (
					<div
						className={cn(
							"absolute inset-y-0 left-0 z-[5]",
							interactive ? "cursor-pointer" : "cursor-default",
						)}
						style={{
							width: `${scrubHitWidthPercent}%`,
							minWidth: "12px",
						}}
						onPointerDown={trackPointerHandlers.onPointerDown}
						onPointerMove={trackPointerHandlers.onPointerMove}
						onPointerUp={trackPointerHandlers.onPointerUp}
						onPointerLeave={trackPointerHandlers.onPointerLeave}
						onPointerCancel={trackPointerHandlers.onPointerCancel}
					/>
				) : null}
				{!interactive && lockedStartPercent < 100 ? (
					<span className="sr-only">{seekLockedLabel}</span>
				) : null}
			</div>
			{trackTooltipLabel != null &&
			trackTooltipAnchorCenterPercent != null ? (
				<MediaPlayerTrackSegmentTooltipLabel
					label={trackTooltipLabel}
					anchorCenterPercent={trackTooltipAnchorCenterPercent}
				/>
			) : null}
		</div>
	);
}
